import { FeatureFlag } from '@revelio/core';
import {
  TRANSITIONS_V2_DATA,
  TransitionsEntityKind,
  type TransitionsReqV2,
  type TransitionV2DataQuery,
  type TransitionV2DataQueryVariables,
} from '@revelio/data-access';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../filters/hooks/useGeographyMode';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { getTransitionV2Filters } from '../../filters/utils/getTransitionV2Filters';
import { useChart } from '../Chart/ChartProvider';
import type {
  PrimaryEntity,
  TransitionChartType,
} from '../PageConfigProvider/types/chart';
import type { TransitionSecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import { PlotFilterType } from '../PageConfigProvider/types/item';
import type { TransitionMetricValues } from '../PageConfigProvider/types/metric';
import { usePageState } from '../PageState/PageStateProvider';
import { isSingleEntityChart } from '../PageState/utils/is-single-entity-chart';
import {
  type BreakdownSingleDataSet,
  type DataProviderChildren,
  DataProviderQueryStatus,
  DataType,
} from './types';
import { convertTransitionData } from './utils/convert-transition-data';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';
import {
  filterEntitiesToFirstEntType,
  keepFirstTransitionEntity,
} from './utils/transition-entity-utils';

// Helper to narrow Transition query variables to the entity chosen in the Primary Entity switcher.
const ENTITY_KIND_MAP: Record<string, TransitionsEntityKind | undefined> = {
  company: TransitionsEntityKind.Company,
  rics_k10: TransitionsEntityKind.RicsK10,
  rics_k400: TransitionsEntityKind.RicsK400,
  region: TransitionsEntityKind.Region,
  country: TransitionsEntityKind.Country,
  msa: TransitionsEntityKind.Msa,
  role_k7: TransitionsEntityKind.RoleK7,
  role_k150: TransitionsEntityKind.RoleK150,
  role_k1500: TransitionsEntityKind.RoleK1500,
};

const numericId = (id: string): number => {
  const parts = id.split('_');
  return Number(parts[parts.length - 1]);
};

const selectPrimaryEntityForTransition = ({
  baseVariables,
  selectedEntity,
  requiresSelection,
  isNewDashboardLayout,
  hasPrimaryEntitySwitcher,
}: {
  baseVariables: ReturnType<typeof getTransitionV2Filters>;
  selectedEntity?: PrimaryEntity;
  requiresSelection: boolean;
  isNewDashboardLayout: boolean;
  hasPrimaryEntitySwitcher: boolean;
}): TransitionsReqV2 | null => {
  if (!baseVariables) return null;

  // When the chart exposes a primary-entity switcher we share one query
  // across every switcher option so toggling between entities doesn't
  // trigger a refetch. We group baseVariables by the EntType the switcher
  // is currently pointing at (or fall back to the first EntType) — same
  // EntType selections reuse the response; cross-EntType switches still
  // refetch because the Transitions API requires a single EntType per req.
  if (hasPrimaryEntitySwitcher) {
    const selectedEntType = selectedEntity
      ? ENTITY_KIND_MAP[selectedEntity.levelId]
      : undefined;

    if (selectedEntType) {
      const sameTypeEntities = baseVariables.entities.filter(
        (e) => e.EntType === selectedEntType
      );
      if (sameTypeEntities.length > 0) {
        return { entities: sameTypeEntities, filters: baseVariables.filters };
      }
    }

    return filterEntitiesToFirstEntType(baseVariables);
  }

  const overridden = (() => {
    if (!requiresSelection) return baseVariables;
    if (!selectedEntity) {
      const first = baseVariables.entities[0];
      return first
        ? { entities: [first], filters: baseVariables.filters }
        : baseVariables;
    }

    const EntType = ENTITY_KIND_MAP[selectedEntity.levelId];
    if (!EntType) return baseVariables;

    return {
      entities: [{ EntID: numericId(selectedEntity.id), EntType }],
      filters: baseVariables.filters,
    };
  })();

  if (isNewDashboardLayout) {
    return filterEntitiesToFirstEntType(overridden);
  }

  return keepFirstTransitionEntity(overridden);
};

export type TransitionData = BreakdownSingleDataSet;

export const TransitionDataProvider = ({
  primaryDimension,
  metric,
  chartType,
  secondaryDimension,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  metric: TransitionMetricValues;
  secondaryDimension?: TransitionSecondaryDimensionValues;
  chartType: TransitionChartType;
  children: DataProviderChildren<TransitionData | undefined>;
}) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const geographyMode = useGeographyMode();
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);

  const { item } = useChart();
  const { pageState } = usePageState();
  const selectedEntity = pageState.primaryEntities?.[item?.id];

  const topNFilterValue = item?.plot?.plotFilters?.find(
    (filter) => filter.type === PlotFilterType.TOP_N
  )?.value;
  const limit =
    typeof topNFilterValue === 'number' ? topNFilterValue : undefined;

  const baseVariables = getTransitionV2Filters(
    effectiveFilters,
    primaryDimension
  );

  const hasMixedSelectedEntityTypes = baseVariables
    ? new Set(baseVariables.entities.map((e) => e.EntType)).size > 1
    : false;

  const hasPrimaryEntitySwitcher = !!item?.plot?.primaryEntitySwitcher;
  const requiresSelection =
    hasPrimaryEntitySwitcher || (item ? isSingleEntityChart(item) : false);

  // Transitions API requires a single EntType
  if (
    hasMixedSelectedEntityTypes &&
    !requiresSelection &&
    !isNewDashboardLayout
  ) {
    const unsupportedData: BreakdownSingleDataSet = {
      type: DataType.BREAKDOWN_SINGLE,
      data: [],
    };

    return children({
      data: unsupportedData,
      status: DataProviderQueryStatus.Success,
    });
  }

  const baseVariablesForTransition = selectPrimaryEntityForTransition({
    baseVariables,
    selectedEntity,
    requiresSelection,
    isNewDashboardLayout,
    hasPrimaryEntitySwitcher,
  });

  if (!baseVariablesForTransition) return null;

  const variables: TransitionsReqV2 = {
    ...baseVariablesForTransition,
    ...(geographyMode === 'administrative' && {
      options: { geo_breakdown_state_over_msa: true },
    }),
  };

  return (
    <TransitionQuery variables={{ req: variables }}>
      {({ data, status }) => {
        return children({
          data: data?.transitionsV2
            ? convertTransitionData({
                data: data.transitionsV2,
                metric,
                chartType,
                secondaryDimension,
                limit,
                selectedEntity: hasPrimaryEntitySwitcher
                  ? selectedEntity
                  : undefined,
              })
            : undefined,
          status,
        });
      }}
    </TransitionQuery>
  );
};

const TransitionQuery = ({
  variables,
  children,
}: {
  variables: TransitionV2DataQueryVariables;
  children: DataProviderChildren<TransitionV2DataQuery | undefined>;
}) => {
  const [{ data, status }] = useUrqlQueryWithStatus<TransitionV2DataQuery>({
    query: TRANSITIONS_V2_DATA,
    variables,
  });

  return children({ data, status });
};
